import { forwardRef } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { FormItem as AntFormItem } from '@/features/vfo/uikit/Form';
import { Select } from '@/features/vfo/uikit/Select';
import { ApiCaseResponseV2 } from '@/shared/api';
import { breakpoints, Input, media } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { COLORS } from '@/shared/constants/colors';
import { UploadFile } from '@/shared/uikitV2/UploadFile';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { ACCEPT_FILE_TYPES, VfoFileFormParams } from '.';

const TITLE_MAX_LENGTH = 100;

type FormItemProps = {
  index: number;
  control: Control<VfoFileFormParams>;
  cases: ApiCaseResponseV2[];
  setValue: UseFormSetValue<VfoFileFormParams>;
  files: VfoFileFormParams['files'];
  caseId?: string;
};

export const FormItem = forwardRef<HTMLDivElement, FormItemProps>(
  ({ index, control, cases, setValue, files, caseId }, ref) => {
    const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadVfoDocument' });

    return (
      <FormItemWrapper ref={ref}>
        <Controller
          name={`files.${index}.document`}
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <UploadFileStyled
                icon="upload-simple"
                title={t('dragAndDrop')}
                description={t('description')}
                maxCount={1}
                fileList={field.value ? [field.value] : []}
                hideDropArea={Boolean(field.value)}
                accept={ACCEPT_FILE_TYPES.join(',')}
                {...field}
                onChange={(data) => {
                  const file = data.fileList[0];
                  if (file) {
                    file.status = 'done';
                    file.percent = 100;
                    field.onChange(file);
                    return;
                  }

                  setValue(`files`, files?.filter((_, i) => i !== index));
                }}
                onRemove={() => {}}
              />
            );
          }}
        />
        <Controller
          name={`files.${index}.title`}
          control={control}
          rules={{
            required: t('fields.title.error'),
            maxLength: {
              value: TITLE_MAX_LENGTH,
              message: t('fields.title.maxLength'),
            },
          }}
          render={({ field, fieldState }) => {
            return (
              <FieldContainerIw label={t(`fields.title.label`)} required>
                <AntFormItem
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input
                    placeholder={t(`fields.title.placeholder`)}
                    type={'text'}
                    maxLength={TITLE_MAX_LENGTH}
                    {...field}
                  />
                  <CharacterCount>
                    {(field.value || '').length}/{TITLE_MAX_LENGTH}
                  </CharacterCount>
                </AntFormItem>
              </FieldContainerIw>
            );
          }}
        />

        <Fields>
          <Controller
            name={`files.${index}.case`}
            control={control}
            rules={{ required: t('fields.case.error') }}
            defaultValue={caseId}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.case.label`)} required>
                  <AntFormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Select
                      placeholder={t(`fields.case.placeholder`)}
                      options={cases.map((item) => ({
                        label: item.title,
                        value: item.id,
                      }))}
                      filterOption={(inputValue, item) => {
                        if (!item) return false;
                        return String(item.label)?.toLowerCase().includes(inputValue.toLowerCase());
                      }}
                      showSearch
                      onChange={field.onChange}
                      value={field.value || null}
                    />
                  </AntFormItem>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={`files.${index}.country`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.country.label`)}>
                  <AntFormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry {...field} />
                  </AntFormItem>
                </FieldContainerIw>
              );
            }}
          />
        </Fields>
      </FormItemWrapper>
    );
  },
);

FormItem.displayName = 'FormItem';

const FormItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${COLORS.white};
  border-radius: 16px;
`;

const UploadFileStyled = styled(UploadFile)`
  .ant-upload {
    background: ${COLORS.systemLight200}!important;
  }
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  align-items: start;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CharacterCount = styled.span`
  display: block;
  font-size: 12px;
  color: ${COLORS.systemDark200};
  margin-top: 4px;
  text-align: right;
`;
